import { useCreateFormatTime } from '@/hooks'
import { Avatar, TypographyProps } from '@mui/material'
import { ConversationInteraction } from '@workwave-tidal/lab/Conversation'
import { useMemo } from 'react'
import { LoadingComponent } from './LoadingComponent'

interface Props {
  item: uui.domain.server.rm.routingAssistant.HistoryItem
  loading: boolean
}

export function Interaction(props: Props) {
  const { item, loading } = props
  const formatTime = useCreateFormatTime()

  const isBot =
    (item.type === 'message' && item.role === 'assistant') ||
    item.type === 'function_call' ||
    item.type === 'function_call_output'

  const avatar = isBot ? <Avatar alt="Miles" src="/images/miles-avatar-android.png" /> : null
  const variant = isBot ? 'outlined' : 'default'
  const alignment = isBot ? 'left' : 'right'

  const messageTypography = useMemo<Partial<TypographyProps> | undefined>(() => {
    switch (item.type) {
      case 'function_call':
      case 'function_call_output':
        return {
          variant: 'body1',
          sx: {
            background: `linear-gradient(135deg, #b3b3b3, #5E5E5E, #b3b3b3)`,
            backgroundClip: 'text',
            backgroundSize: '200% 100%',
            animation: `shimmer 2s linear infinite`,
            fontStyle: 'italic',
            color: 'transparent',
            '@keyframes shimmer': {
              '0%': {
                backgroundPosition: '200% 0',
              },
              '100%': {
                backgroundPosition: '-200% 0',
              },
            },
          },
        }

      default:
        return
    }
  }, [item])

  const message = useMemo<string>(() => {
    switch (item.type) {
      case 'function_call':
        return item.name
      case 'function_call_output':
        return item.output
      case 'message':
        return typeof item.content === 'string'
          ? item.content
          : item.content.map(c => c.text).join('\n')
      default:
        return item.content
    }
  }, [item])

  const id = useMemo<string>(() => {
    switch (item.type) {
      case 'function_call':
        return item.id
      case 'function_call_output':
        return item.call_id
      case 'message':
        return item.id ?? ''
      default:
        return ''
    }
  }, [item])

  const senderName = useMemo<string>(() => {
    switch (item.type) {
      case 'function_call':
      case 'function_call_output':
        return 'Thinking...'

      case 'message':
        return isBot ? 'Miles - AI Routing Assistant' : ''
    }

    return ''
  }, [item, isBot])

  return (
    <ConversationInteraction
      loadingComponent={<LoadingComponent alignment={alignment} />}
      alignment={alignment}
      avatar={avatar}
      contentType="text"
      id={id}
      loading={loading}
      time={formatTime(new Date(item.timestamp), true)}
      slotProps={{
        messageTypography,
      }}
      message={message}
      sender={senderName}
      variant={variant}
    />
  )
}
